/**
 * Mantra MFS110 fingerprint sensor — browser-side driver.
 *
 * Windows PCs at each cafe run the Mantra MFS110 Client Service (or the
 * generic MFS100 RD/enrollment service) which exposes a local HTTP/HTTPS
 * server. This module discovers that local service, calls its `capture`
 * endpoint with the standard PidOptions XML, and parses the response into
 * a base64 ISO template + quality score.
 *
 * IMPORTANT: this only works in a real cafe browser where the Mantra
 * driver + client service are installed and the self-signed localhost
 * cert has been accepted. It will hard-fail in the Emergent preview.
 */

// Try HTTPS localhost:8003/mfs100 first (Mantra's default Client Service
// port), then fall back to the RD-service family (11100-11120). This
// matches the two builds Mantra ships in the wild.
const CANDIDATE_ENDPOINTS = [
  { base: "https://localhost:8003/mfs100", info: "/info", capture: "/capture" },
  ...Array.from({ length: 21 }, (_, i) => ({
    base: `http://127.0.0.1:${11100 + i}`,
    info: "/rd/info",
    capture: "/rd/capture",
  })),
  ...Array.from({ length: 21 }, (_, i) => ({
    base: `http://127.0.0.1:${11100 + i}`,
    info: "/info",
    capture: "/capture",
  })),
];

// Cached discovery result for the current tab session.
let _cachedEndpoint = null;

function withTimeout(promise, ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, cleanup: () => clearTimeout(timer) };
}

async function probe(endpoint) {
  const { signal, cleanup } = withTimeout({}, 1200);
  try {
    const res = await fetch(endpoint.base + endpoint.info, {
      method: "GET",
      signal,
    });
    // Any 2xx/3xx/4xx is enough proof the port is reachable — some builds
    // respond 400 to a GET on /capture but 200 on /info, others the reverse.
    return res.status < 500;
  } catch {
    return false;
  } finally {
    cleanup();
  }
}

/** Discover the local Mantra service. Returns { base, capture } or throws. */
export async function discoverMantraService({ signal } = {}) {
  if (_cachedEndpoint) return _cachedEndpoint;
  for (const ep of CANDIDATE_ENDPOINTS) {
    if (signal?.aborted) throw new Error("discovery-aborted");
    // eslint-disable-next-line no-await-in-loop
    if (await probe(ep)) {
      _cachedEndpoint = ep;
      return ep;
    }
  }
  throw new Error(
    "Couldn't find the Mantra MFS110 service on this PC. Make sure the Mantra Client Service is running, the sensor is plugged in, and the localhost certificate has been accepted (open https://localhost:8003/mfs100 once and accept it).",
  );
}

/** Reset the cached endpoint — call from a "Reconnect scanner" affordance. */
export function forgetMantraService() {
  _cachedEndpoint = null;
}

/** Standard PID options envelope. `fCount=1` = capture 1 fingerprint,
 * `fType=2` = FMR (ISO 19794-2 minutiae template), `format=0` = XML. */
function pidOptions({ timeoutMs = 10000 } = {}) {
  return `<PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" iType="0" format="0" pidVer="2.0" timeout="${timeoutMs}" posh="UNKNOWN" env="P" /></PidOptions>`;
}

// Mantra builds differ — support every common tag we've seen in the wild.
function extractFirst(xml, tags) {
  for (const t of tags) {
    // Attribute-style: <Data ISOTemplate="..." Quality="80" />
    const attr = xml.match(new RegExp(`\\b${t}="([^"]+)"`));
    if (attr && attr[1]) return attr[1];
    // Element-style: <ISOTemplate>...</ISOTemplate>
    const el = xml.match(new RegExp(`<${t}[^>]*>([\\s\\S]*?)</${t}>`));
    if (el && el[1] && el[1].trim()) return el[1].trim();
  }
  return "";
}

function parseCaptureResponse(text) {
  // Trim BOM / whitespace some builds prepend.
  const xml = (text || "").trim();
  const errCode = extractFirst(xml, ["errCode", "ErrorCode"]);
  const errInfo = extractFirst(xml, ["errInfo", "ErrorDescription"]);
  if (errCode && errCode !== "0" && errCode !== "" && errCode !== "0.0") {
    throw new Error(errInfo || `Fingerprint capture failed (code ${errCode})`);
  }
  const template = extractFirst(xml, [
    "IsoTemplate",
    "ISOTemplate",
    "FMRValue",
    "Template",
    "Data",
  ]);
  const quality = Number(
    extractFirst(xml, ["Quality", "qScore", "NFIQ"]) || 0,
  );
  if (!template) {
    throw new Error(
      "No fingerprint template returned. Make sure a finger was pressed on the sensor.",
    );
  }
  return { template, quality };
}

/** Capture one fingerprint. Returns `{ template: base64, quality: 0-100 }`. */
export async function captureFingerprint({ timeoutMs = 10000 } = {}) {
  const ep = await discoverMantraService();
  const res = await fetch(ep.base + ep.capture, {
    method: "POST",
    headers: { "Content-Type": "text/xml" },
    body: pidOptions({ timeoutMs }),
  });
  if (!res.ok) {
    throw new Error(
      `Mantra service returned HTTP ${res.status}. Restart the Mantra Client Service and try again.`,
    );
  }
  const text = await res.text();
  return parseCaptureResponse(text);
}

// Score threshold recommended by Mantra's MFS100 SDK docs for a genuine
// finger match. Callers can pass a higher threshold for stricter matching.
export const DEFAULT_MATCH_THRESHOLD = 14000;

function parseMatchResponse(text) {
  const xml = (text || "").trim();
  const scoreStr =
    extractFirst(xml, ["Score", "MatchScore", "score", "MatchingScore"]) || "0";
  const score = Number(scoreStr) || 0;
  const errCode = extractFirst(xml, ["errCode", "ErrorCode"]);
  if (errCode && errCode !== "0" && errCode !== "0.0") {
    // The match endpoint itself errored (e.g., bad template). Treat as
    // "no match" but bubble the error so callers can log it.
    return { score: 0, error: extractFirst(xml, ["errInfo", "ErrorDescription"]) };
  }
  return { score };
}

/** Ask the local Mantra service to biometric-match a probe template
 * against a stored gallery template. Returns `{ score, matched }`.
 * Falls back to `{ score: 0, matched: false, unsupported: true }` if the
 * local service doesn't expose a match endpoint — the caller should then
 * rely on the backend's exact-hash uniqueness check as the safety net. */
export async function matchTemplates(probe, gallery, {
  threshold = DEFAULT_MATCH_THRESHOLD,
} = {}) {
  if (!probe || !gallery) return { score: 0, matched: false };
  const ep = await discoverMantraService();
  // Mantra builds expose the matcher under different paths — try each
  // until one responds with a real score.
  const candidates = [
    `${ep.base}/matchiso`,
    `${ep.base}/match`,
    `${ep.base}/verify`,
  ];
  const body =
    `<Match ver="1.0">` +
    `<ProbeTemplate>${probe}</ProbeTemplate>` +
    `<GalleryTemplate>${gallery}</GalleryTemplate>` +
    `</Match>`;
  for (const url of candidates) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/xml" },
        body,
      });
      if (!res.ok) continue;
      // eslint-disable-next-line no-await-in-loop
      const text = await res.text();
      const { score } = parseMatchResponse(text);
      return { score, matched: score >= threshold };
    } catch {
      // Try the next candidate.
    }
  }
  return { score: 0, matched: false, unsupported: true };
}
